"use client"

import { useState } from "react"
import { Check } from "lucide-react"
import { LegalLink } from "@/components/legal/legal-link"
import apiClient from "@/lib/api/api-client"
import type { User } from "@/types/user-types"

type AgentTermsScreenProps = {
  termsInitiallyAccepted: boolean
  confidentialityInitiallyAccepted: boolean
  /** Players confirm the terms only. Agents also promise confidentiality. */
  showConfidentiality?: boolean
  onAccepted: (user: User) => void
}

function ConfirmBox({
  checked,
  labelledBy,
  onToggle,
}: {
  checked: boolean
  labelledBy: string
  onToggle: () => void
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      aria-labelledby={labelledBy}
      onClick={onToggle}
      className={
        "flex h-6 w-6 shrink-0 items-center justify-center rounded-[6px] border-2 p-0 " +
        "focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-[#3B5FC4] dark:focus-visible:outline-primary " +
        (checked
          ? "border-[#3B5FC4] bg-[#3B5FC4] text-white dark:border-primary dark:bg-primary"
          : "border-[#9CA3AF] bg-white dark:border-muted-foreground/70 dark:bg-card")
      }
    >
      {checked ? <Check className="h-3.5 w-3.5" strokeWidth={3.5} aria-hidden /> : null}
    </button>
  )
}

export function AgentTermsScreen({
  termsInitiallyAccepted,
  confidentialityInitiallyAccepted,
  showConfidentiality = true,
  onAccepted,
}: AgentTermsScreenProps) {
  const [terms, setTerms] = useState(termsInitiallyAccepted)
  const [confidentiality, setConfidentiality] = useState(confidentialityInitiallyAccepted)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const confidentialityOk = !showConfidentiality || confidentiality
  const canContinue = terms && confidentialityOk && !submitting

  async function continueToApp() {
    if (!terms || !confidentialityOk || submitting) return
    setSubmitting(true)
    setError(null)
    try {
      const user = await apiClient.users.acceptLegal(showConfidentiality)
      onAccepted(user)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save your confirmation.")
      setSubmitting(false)
    }
  }

  return (
    <div className="flex min-h-dvh flex-col bg-white text-[#1F2328] dark:bg-background dark:text-foreground md:items-center md:justify-center md:bg-[#F3F4F6] md:px-6 md:py-8">
      <div className="flex min-h-dvh w-full flex-1 flex-col gap-[20px] p-5 pt-[max(1.25rem,env(safe-area-inset-top))] pb-[max(1.25rem,env(safe-area-inset-bottom))] md:min-h-[min(780px,calc(100dvh-4rem))] md:w-full md:max-w-[540px] md:flex-none md:rounded-[16px] md:border md:border-[#E5E7EB] md:bg-white md:shadow-sm md:dark:border-border md:dark:bg-card">
        <h1 className="m-0 text-[22px] font-bold leading-[1.25] tracking-[-0.02em]">Welcome to SigmAI</h1>

        <section className="flex flex-1 flex-col gap-[16px]">
          <p className="m-0 text-[14px] leading-[1.55] text-[#4B5563] dark:text-muted-foreground">
            Before you start, please confirm the following.
          </p>
          <div className="text-[12px] font-bold uppercase tracking-[0.06em]">Required</div>

          <div className="flex items-start gap-[12px] rounded-[12px] border border-[#E5E7EB] p-[14px] dark:border-border">
            <ConfirmBox
              checked={terms}
              labelledBy="lbl-terms"
              onToggle={() => setTerms((value) => !value)}
            />
            <span id="lbl-terms" className="pt-px text-[15px] leading-[1.45]">
              I accept the{" "}
              <LegalLink
                href="/terms"
                className="text-[#3B5FC4] hover:text-[#2C4A9E] focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-[#3B5FC4] dark:text-primary dark:hover:text-primary/80 dark:focus-visible:outline-primary"
              >
                Terms of Use
              </LegalLink>{" "}
              and have read the{" "}
              <LegalLink
                href="/privacy"
                className="text-[#3B5FC4] hover:text-[#2C4A9E] focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-[#3B5FC4] dark:text-primary dark:hover:text-primary/80 dark:focus-visible:outline-primary"
              >
                Privacy Statement
              </LegalLink>
              .
            </span>
          </div>

          {showConfidentiality ? (
          <div className="flex items-start gap-[12px] rounded-[12px] border border-[#E5E7EB] p-[14px] dark:border-border">
            <ConfirmBox
              checked={confidentiality}
              labelledBy="lbl-conf"
              onToggle={() => setConfidentiality((value) => !value)}
            />
            <span id="lbl-conf" className="pt-px text-[15px] leading-[1.45]">
              I will only use my players&apos; data to support them. I keep it confidential and do not share it with clubs, scouts, media or others without the player&apos;s consent.
            </span>
          </div>
          ) : null}
        </section>

        <div className="flex items-center justify-between gap-2 border-t border-[#E5E7EB] pt-[16px] dark:border-border">
          <span className="w-[104px]" aria-hidden />
          <div className="flex flex-col items-end gap-2">
            {error ? <p className="m-0 text-right text-sm text-red-600 dark:text-red-400">{error}</p> : null}
            <button
              type="button"
              onClick={continueToApp}
              aria-disabled={!canContinue}
              className={
                "min-h-[44px] rounded-[10px] border-0 px-5 text-[15px] font-semibold " +
                "focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-[#3B5FC4] " +
                (canContinue
                  ? "cursor-pointer bg-[#3B5FC4] text-white hover:bg-[#2C4A9E] dark:bg-primary dark:hover:bg-primary/90"
                  : "cursor-not-allowed bg-[#E5E7EB] text-[#6B7280] dark:bg-muted dark:text-muted-foreground")
              }
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
